import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ArrowUpRightIcon from '@atlaskit/icon/core/arrow-up-right';

import type { AtAGlanceSummaries } from '../../at-a-glance/buildAtAGlanceSummaries.ts';
import { getTopConnectionLinks } from '../../utils/getTopConnectionLinks.ts';
import { ConnectionsByLineSection } from '../connections-by-line-section/ConnectionsByLineSection.tsx';

import type { LinksToData } from './useConnectionSectionsData.ts';
import type { SectionExpansionProps } from './useSectionExpansion.ts';

const i18n = defineMessages({
	linksTo: {
		id: 'byline-connections.preview-panel.section.links-to',
		defaultMessage: 'Links to',
		description:
			'Heading of the Teamwork Graph preview panel section listing the links found on the current page. The section shows the number of links in a badge beside the heading.',
	},
	empty: {
		id: 'byline-connections.preview-panel.section.links-to.empty',
		defaultMessage: 'This content does not link to anything.',
		description:
			'Shown in the expanded "Links to" section of the Teamwork Graph preview panel when the current page contains no links.',
	},
});

export type LinksToSectionProps = LinksToData &
	SectionExpansionProps & {
		/** cc-smarts one-liners keyed by ARI; a row renders the one for its link. */
		aiSummaries?: AtAGlanceSummaries;
		onPreviewOpen?: () => void;
		onAskRovo?: (prompt?: string) => void;
	};

/**
 * The panel's "Links to" section: the links the byline collected on this page, as
 * `useConnectionSectionsData` read them off the outbound bridge.
 */
export const LinksToSection = ({
	links,
	aiSummaries,
	onPreviewOpen,
	onAskRovo,
	isExpanded,
	onToggleExpanded,
}: LinksToSectionProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	// Reorders unconnected links first; the full length means no truncation.
	const items = useMemo(() => getTopConnectionLinks(links, links.length), [links]);

	return (
		<ConnectionsByLineSection
			title={formatMessage(i18n.linksTo)}
			iconBefore={<ArrowUpRightIcon label="" spacing="none" />}
			items={items}
			linkDirection="outgoing"
			aiSummaries={aiSummaries}
			onPreviewOpen={onPreviewOpen}
			onAskRovo={onAskRovo}
			isExpanded={isExpanded}
			onToggleExpanded={onToggleExpanded}
			emptyMessage={formatMessage(i18n.empty)}
			testId="teamwork-graph-preview-panel-links-to-section"
		/>
	);
};
